import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { capitalize } from "@/Helpers/capitalize";
import { formatPhone } from "@/Helpers/formatPhone";
import { formatPrice } from "@/Helpers/formatPrice";
import { priceBeforeTax } from "@/Helpers/priceBeforeTax";
import { formatAddress } from "../../Helpers/quotesFormatLocation";
import { QuotesColumnSectionHeader } from "@/Shared/Quotes/CustomComponents/QuotesColumnSectionHeader";
import { QuotesModifyLink } from "@/Shared/Quotes/CustomComponents/QuotesModifyLink";
import { QuotesPrimaryText } from "@/Shared/Quotes/CustomComponents/QuotesPrimaryText";
import { QuotesSecondaryText } from "@/Shared/Quotes/CustomComponents/QuotesSecondaryText";
import { QuotesTextLink } from "@/Shared/Quotes/CustomComponents/QuotesTextLink";
import QuotesBeforeCouponTag from "../QuotesBeforeCouponTag";
import type { TransitColumnProps } from "../QuotesTransitColumn";

// Credit terms win over a stored payment method: an invoiced customer never
// has a card charged.
function showCreditTerms(props: TransitColumnProps) {
  return props.paymentTermsDays !== undefined && props.paymentTermsDays > 0;
}

// Every Stack here passes align="start". The enclosing column is
// `items-start`, so each line sized itself to its own content; a Stack
// stretches its children by default, which would widen the gold coupon tag
// and the link rows to the column's full width.
// oxlint-disable-next-line complexity -- grandfathered, see ENG-7672
export function PriceSection_(props: TransitColumnProps) {
  if (!props.showPriceSection) {
    return <></>;
  }

  return (
    <Stack gap={1} align="start">
      {props.taxAmount && props.taxAmount > 0 ? (
        <Stack gap={1} align="start">
          <Stack gap={0} align="start">
            <QuotesColumnSectionHeader>
              Price (before tax)
            </QuotesColumnSectionHeader>
            <QuotesSecondaryText>
              ${formatPrice(priceBeforeTax(props.price!, props.taxAmount))}{" "}
              {props.currency}
            </QuotesSecondaryText>
            <QuotesColumnSectionHeader>
              Total after tax
            </QuotesColumnSectionHeader>
            <QuotesSecondaryText>
              ${formatPrice(props.price!)} {props.currency}
            </QuotesSecondaryText>
          </Stack>
          {/* priceBeforeCoupon is the original tax-inclusive total, so it
           * sits with the after-tax total — the saving equals the coupon. */}
          {props.priceBeforeCoupon && (
            <QuotesBeforeCouponTag
              priceBeforeCoupon={props.priceBeforeCoupon}
              size="medium"
            />
          )}
        </Stack>
      ) : (
        <Stack gap={1} align="start">
          <Stack gap={0} align="start">
            <QuotesColumnSectionHeader>Price</QuotesColumnSectionHeader>
            <QuotesSecondaryText>
              ${formatPrice(props.price!)} {props.currency}
            </QuotesSecondaryText>
          </Stack>
          {props.priceBeforeCoupon && (
            <QuotesBeforeCouponTag
              priceBeforeCoupon={props.priceBeforeCoupon}
              size="medium"
            />
          )}
        </Stack>
      )}
      {showCreditTerms(props) && (
        <Stack gap={0} align="start">
          <QuotesColumnSectionHeader>Credit Terms</QuotesColumnSectionHeader>
          <QuotesSecondaryText>An invoice will be issued.</QuotesSecondaryText>
          <QuotesSecondaryText>
            Payment will be due in {props.paymentTermsDays} days
          </QuotesSecondaryText>
        </Stack>
      )}
      {!showCreditTerms(props) && props.selectedBankAccount && (
        <Stack gap={1} align="start">
          <Stack gap={0} align="start">
            <QuotesColumnSectionHeader>Bank Account</QuotesColumnSectionHeader>
            <QuotesSecondaryText>
              {props.selectedBankAccount.bankName} ending{" "}
              {props.selectedBankAccount.lastFourDigits}
            </QuotesSecondaryText>
          </Stack>
          {props.onChangePaymentMethod && (
            <QuotesModifyLink data-testid="change-payment-method-link">
              <QuotesTextLink onClick={props.onChangePaymentMethod}>
                Change Payment Method
              </QuotesTextLink>
            </QuotesModifyLink>
          )}
        </Stack>
      )}
      {!showCreditTerms(props) && !props.selectedBankAccount && (
        <Stack gap={1} align="start">
          <Stack gap={0} align="start">
            <QuotesColumnSectionHeader>Credit Card</QuotesColumnSectionHeader>
            {props.selectedCard && (
              <QuotesSecondaryText>
                <img
                  className="mr-[2px] max-h-4"
                  src={`/images/credit-card-brands/${props.selectedCard?.brand}.png`}
                  alt={props.selectedCard?.brand}
                />{" "}
                {capitalize(props.selectedCard?.brand || "")} Ending{" "}
                {props.selectedCard?.lastFourDigits}
              </QuotesSecondaryText>
            )}
            {!props.selectedCard && (
              <QuotesSecondaryText>
                ⚠️ No selected credit card
              </QuotesSecondaryText>
            )}
          </Stack>
          {props.onChangePaymentMethod && (
            <QuotesSecondaryText>
              <QuotesModifyLink data-testid="change-payment-method-link">
                <QuotesTextLink onClick={props.onChangePaymentMethod}>
                  Change Payment Method
                </QuotesTextLink>
              </QuotesModifyLink>
            </QuotesSecondaryText>
          )}
        </Stack>
      )}
    </Stack>
  );
}

export function BrokerSection_(props: TransitColumnProps) {
  const { broker } = props;

  if (broker === undefined) {
    return <></>;
  }

  return (
    <Stack gap={1} align="start">
      <Stack gap={0} align="start">
        <QuotesColumnSectionHeader>Customs Broker</QuotesColumnSectionHeader>
        <QuotesPrimaryText>{broker.businessName}</QuotesPrimaryText>
        <QuotesSecondaryText>{broker.contact.contactName}</QuotesSecondaryText>
        <QuotesSecondaryText>
          {formatPhone(
            broker.contact.phoneNumber,
            broker.contact.phoneNumberExtension,
          )}
        </QuotesSecondaryText>
        <QuotesSecondaryText>{broker.contact.emailAddress}</QuotesSecondaryText>
        <QuotesSecondaryText>{broker.address.addressLine}</QuotesSecondaryText>
        <QuotesSecondaryText>{broker.address.addressLine2}</QuotesSecondaryText>
        <QuotesSecondaryText>
          {formatAddress(broker.address)}
        </QuotesSecondaryText>
        {broker.accountNumber && (
          <QuotesSecondaryText>
            Account Number: {broker.accountNumber}
          </QuotesSecondaryText>
        )}
      </Stack>
      {props.onChangeBroker && (
        <QuotesSecondaryText>
          <QuotesModifyLink>
            <QuotesTextLink
              onClick={props.onChangeBroker}
              data-testid="change-broker-link"
            >
              Change Broker
            </QuotesTextLink>
          </QuotesModifyLink>
        </QuotesSecondaryText>
      )}
    </Stack>
  );
}
